// Admin API methods — mixin applied to the ApiService prototype chain.
// Methods run with `this` bound to the ApiService instance, so this.request()
// and cross-domain calls resolve through the shared HttpClient base.
const withAdminApi = (Base) => class extends Base {
  // Students endpoints (for instructors/admins)

  async getStudentProfile(studentId) {
    return this.request(`/students/${studentId}`);
  }

  async getGradebook(courseId = null) {
    const query = courseId ? `?courseId=${courseId}` : '';
    return this.request(`/students/gradebook/all${query}`);
  }

  async overrideAssignmentGrade(assignmentId, submissionId, grade, feedback, override, overrideNotes) {
    return this.request(`/assignments/${assignmentId}/submissions/${submissionId}/grade`, {
      method: 'PUT',
      body: JSON.stringify({ grade, feedback, override, overrideNotes })
    });
  }

  async gradeUnsubmittedAssignment(assignmentId, studentId, grade, feedback, override, overrideNotes) {
    return this.request(`/assignments/${assignmentId}/students/${studentId}/grade`, {
      method: 'POST',
      body: JSON.stringify({ grade, feedback, override, overrideNotes })
    });
  }

  async assignCourseGrade(courseId, studentId, grade, notes, numericGrade = null) {
    return this.request(`/courses/${courseId}/students/${studentId}/grade`, {
      method: 'PUT',
      body: JSON.stringify({ grade, notes, numericGrade })
    });
  }

  async overrideQuizGrade(assignmentId, attemptId, score, notes) {
    // Only allow overriding grades for submitted quizzes (must have attemptId)
    if (!attemptId) {
      throw new Error('Cannot grade quiz: Quiz must be submitted before it can be graded');
    }
    
    // Override existing attempt score
    return this.request(`/quizzes/attempts/${attemptId}/score/override`, {
      method: 'PUT',
      body: JSON.stringify({ score, notes })
    });
  }

  async gradeUnsubmittedQuiz(assignmentId, studentId, score, notes) {
    return this.request(`/quizzes/assignments/${assignmentId}/students/${studentId}/grade`, {
      method: 'POST',
      body: JSON.stringify({ score, notes })
    });
  }

  // Additional utility methods
  async getCurrentUserProfile() {
    return this.request('/users/profile');
  }

  getCurrentUser() {
    // Get current user from localStorage (for client-side use)
    const userStr = localStorage.getItem('faithinsight_user');
    return userStr ? JSON.parse(userStr) : null;
  }

  async updateProfile(profileData) {
    return this.request('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData)
    });
  }

  // User management endpoints (for admins)
  async getUsers() {
    return this.request('/users');
  }

  async getUsersWithDefaultPasswords() {
    return this.request('/users/default-passwords');
  }

  async sendPasswordReminder(userId) {
    return this.request('/users/send-password-reminder', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userId })
    });
  }

  async sendCSVEmail(data) {
    return this.request('/courses/send-csv-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
  }

  async sendAttendanceCSVEmail(data) {
    return this.request('/attendance/send-csv-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
  }

  async getInstructors() {
    const response = await this.request('/users');
    if (response.success && response.users) {
      const instructors = response.users.filter(user => user.role === 'instructor');
      return { success: true, instructors };
    }
    return { success: false, instructors: [] };
  }

  async createUser(userData) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async updateUser(userId, userData) {
    return this.request(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }

  async deleteUser(userId) {
    return this.request(`/users/${userId}`, {
      method: 'DELETE'
    });
  }

  async resetUserPassword(userId, sendEmail = true) {
    return this.request(`/admin/users/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ sendEmail })
    });
  }


  // Course management endpoints (for admins/instructors)
  async createCourse(courseData) {
    return this.request('/courses', {
      method: 'POST',
      body: JSON.stringify(courseData)
    });
  }

  async updateCourse(courseId, courseData) {
    return this.request(`/courses/${courseId}`, {
      method: 'PUT',
      body: JSON.stringify(courseData)
    });
  }

  async deleteCourse(courseId) {
    return this.request(`/courses/${courseId}`, {
      method: 'DELETE'
    });
  }

  // HLS backfill tool — admin-only listing/submission of recordings that
  // predate HLS output existing in the transcode pipeline.

  async getHlsBackfillRecordings({ status = 'all', search = '', page = 1, pageSize = 50 } = {}) {
    const params = new URLSearchParams({ status, page: String(page), pageSize: String(pageSize) });
    if (search) params.set('search', search);
    return this.request(`/admin/hls-backfill?${params.toString()}`);
  }

  async submitHlsBackfill(contentIds) {
    // Each id submits a real sequential AWS CreateJob call server-side —
    // callers should chunk large batches (see submitInChunks.js), but raise
    // the timeout too as a safety margin for a slow chunk.
    return this.request('/admin/hls-backfill/submit', {
      method: 'POST',
      body: JSON.stringify({ contentIds }),
      timeout: 30000
    });
  }

  async deleteHlsBackfillRecordings(contentIds) {
    return this.request('/admin/hls-backfill/delete', {
      method: 'POST',
      body: JSON.stringify({ contentIds })
    });
  }

  // S3 orphan discovery — video files in the serving bucket with no matching
  // course_content row. scanS3Orphans pages through the bucket (the caller
  // drives the loop via continuationToken); getTrackedS3Orphans reads back
  // previously-found/submitted ones without rescanning.

  async scanS3Orphans({ continuationToken } = {}) {
    // POST with the token in the body, not a GET query string — S3
    // continuation tokens are opaque/high-entropy and some WAFs (e.g.
    // ModSecurity on shared/cPanel hosting) flag them as suspicious in a
    // query string. Each page also does an S3 list call plus two batch DB
    // lookups — slower than a typical API call, so the timeout is raised too.
    return this.request('/admin/hls-backfill/s3-orphans/scan', {
      method: 'POST',
      body: JSON.stringify({ continuationToken }),
      timeout: 30000
    });
  }

  async getTrackedS3Orphans() {
    return this.request('/admin/hls-backfill/s3-orphans/tracked');
  }

  async submitS3OrphanBackfill(keys) {
    // Same rationale as submitHlsBackfill — chunk on the caller side, raise
    // the timeout as a safety margin.
    return this.request('/admin/hls-backfill/s3-orphans/submit', {
      method: 'POST',
      body: JSON.stringify({ keys }),
      timeout: 30000
    });
  }

};

export default withAdminApi;
